// Shared media-device selection.
//
// The verification screen (BiometricCheck) lets the student pick a specific
// camera & microphone. We persist those deviceIds here so that EVERY subsequent
// getUserMedia() call during the assessment uses the SAME devices, instead of
// silently falling back to the browser default. Stored in sessionStorage so the
// selection survives the route change from verification -> assessment but is
// cleared when the tab closes.

const STORAGE_KEY = 'assessment_selected_media_devices';

export const getSelectedMediaDevices = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { videoDeviceId: '', audioDeviceId: '' };
    const parsed = JSON.parse(raw);
    return {
      videoDeviceId: parsed.videoDeviceId || '',
      audioDeviceId: parsed.audioDeviceId || '',
    };
  } catch (e) {
    return { videoDeviceId: '', audioDeviceId: '' };
  }
};

export const setSelectedMediaDevices = ({ videoDeviceId, audioDeviceId } = {}) => {
  try {
    const current = getSelectedMediaDevices();
    const next = {
      videoDeviceId: videoDeviceId !== undefined ? videoDeviceId : current.videoDeviceId,
      audioDeviceId: audioDeviceId !== undefined ? audioDeviceId : current.audioDeviceId,
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch (e) {
    // sessionStorage may be unavailable (e.g. private mode) — selection just
    // won't persist, callers fall back to default behaviour.
  }
};

export const clearSelectedMediaDevices = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // ignore
  }
};

// Merge a chosen deviceId into a single track constraint.
//  - track === true        -> { deviceId: { exact } }
//  - track is an object     -> { ...track, deviceId: { exact } }
//  - track === false / null -> left untouched (track not requested)
const mergeTrack = (track, deviceId) => {
  if (!deviceId) return track; // nothing selected -> leave as-is
  if (track === false || track == null) return track; // track not requested
  const base = track === true ? {} : { ...track };
  base.deviceId = { exact: deviceId };
  return base;
};

// Inject the student's selected deviceIds into a getUserMedia constraints object.
export const applySelectedDevices = (constraints = {}) => {
  const { videoDeviceId, audioDeviceId } = getSelectedMediaDevices();
  const next = { ...constraints };
  if ('video' in next) next.video = mergeTrack(next.video, videoDeviceId);
  if ('audio' in next) next.audio = mergeTrack(next.audio, audioDeviceId);
  return next;
};

// Drop-in replacement for navigator.mediaDevices.getUserMedia that pins the
// student's chosen camera/mic. If the chosen device has since been unplugged
// (OverconstrainedError / NotFoundError) it transparently retries with the
// original constraints so the assessment is never hard-blocked.
export const getUserMediaWithSelectedDevices = async (constraints = {}) => {
  const pinned = applySelectedDevices(constraints);
  try {
    return await navigator.mediaDevices.getUserMedia(pinned);
  } catch (err) {
    if (err && (err.name === 'OverconstrainedError' || err.name === 'NotFoundError')) {
      return await navigator.mediaDevices.getUserMedia(constraints);
    }
    throw err;
  }
};
